import { Loading } from '@/components/loaders';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import type { UseConnectorFlowRunnerResult } from '@/features/connectors/flows';
import { useEmailConnectorFlowRunner } from '@/features/connectors/email';
import { useSmsConnectorFlowRunner } from '@/features/connectors/sms';
import { useWhatsappConnectorFlowRunner } from '@/features/connectors/whatsapp';
import { showToast } from '@/features/toasts/toastsSlice';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getEditFlowStepHandler, type EditFlowSelection } from './editFlow';

type SilentTemplateDeleteProps = {
  selection: EditFlowSelection;
  onExit: (deleted: boolean) => void;
};

type RunnerSlice = Pick<
  UseConnectorFlowRunnerResult,
  'prepareResult' | 'prepareError' | 'isPreparePending' | 'createInstance'
> & {
  connectorFlowId: string | undefined;
  connectorInstanceId: string | undefined;
  isChannelLoading: boolean;
};

/**
 * After the user confirms delete, run the channel Delete Template flow with autofill/skip so no dropdown is shown.
 */
const SilentTemplateDeleteBody: FC<{
  selection: EditFlowSelection;
  onExit: (deleted: boolean) => void;
  runner: RunnerSlice;
}> = ({ selection, onExit, runner }) => {
  const { isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const startedRef = useRef(false);
  const exitedOnErrorRef = useRef(false);

  const { prepareResult, prepareError, isPreparePending, createInstance, connectorFlowId, connectorInstanceId, isChannelLoading } =
    runner;

  // Prepare failure: toast and return to the list (do not trap the user on this screen).
  useEffect(() => {
    if (!prepareError || exitedOnErrorRef.current) return;
    exitedOnErrorRef.current = true;
    appDispatch(showToast({ type: ToastType.ERROR, title: String(t('modelli.editor.deleteFailed')) }));
    onExit(false);
  }, [appDispatch, onExit, prepareError, t]);

  useEffect(() => {
    if (startedRef.current || exitedOnErrorRef.current) return;
    if (!prepareResult || !connectorFlowId || !connectorInstanceId) return;

    const step1 = (prepareResult as Record<string, { reactJsonSchemaForm?: { formId?: string; schema?: unknown; uiSchema?: Record<string, unknown> } }>)[
      'step1'
    ];
    const formSchema = step1?.reactJsonSchemaForm;
    if (!formSchema) {
      exitedOnErrorRef.current = true;
      appDispatch(showToast({ type: ToastType.ERROR, title: String(t('modelli.editor.deleteFailed')) }));
      onExit(false);
      return;
    }

    const autofill = getEditFlowStepHandler(selection.channel, selection)({
      stepId: 'step1',
      stepIndex: 0,
      formSchema: {
        formId: formSchema.formId,
        schema: formSchema.schema as any,
        uiSchema: formSchema.uiSchema,
      },
      accumulatedData: {},
    });

    if (!autofill) {
      exitedOnErrorRef.current = true;
      appDispatch(showToast({ type: ToastType.ERROR, title: String(t('modelli.editor.deleteFailed')) }));
      onExit(false);
      return;
    }

    startedRef.current = true;
    void (async () => {
      const result = await createInstance(autofill.data, {});
      if (!result.success) {
        appDispatch(
          showToast({
            type: ToastType.ERROR,
            title: result.message ?? String(tCampaign('flowInstanceCreationFailed')),
          })
        );
        onExit(false);
        return;
      }
      appDispatch(showToast({ type: ToastType.SUCCESS, title: String(t('modelli.editor.deleteSuccess')) }));
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      onExit(true);
    })();
  }, [appDispatch, connectorFlowId, connectorInstanceId, createInstance, onExit, prepareResult, selection, t, tCampaign]);

  return (
    <div className='space-y-4'>
      <button type='button' onClick={() => onExit(false)} className='text-sm font-semibold text-brand-700 hover:underline'>
        ← {t('modelli.editor.backToList')}
      </button>

      {isAudienceLoading || isChannelLoading || isPreparePending ? (
        <Loading />
      ) : isAudienceSettled && !hasAudience ? (
        <AudienceRequiredEmptyState />
      ) : prepareError ? (
        <p className='text-sm text-tertiary-600' role='alert'>
          {t('modelli.editor.deleteFailed')}
        </p>
      ) : (
        <Loading />
      )}
    </div>
  );
};

const WhatsappSilentTemplateDelete: FC<SilentTemplateDeleteProps> = ({ selection, onExit }) => {
  const { audienceId } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const runner = useWhatsappConnectorFlowRunner('templateDelete', {
    audienceId,
    lang: currentLanguage,
  });
  return <SilentTemplateDeleteBody selection={selection} onExit={onExit} runner={runner} />;
};

const SmsSilentTemplateDeleteInner: FC<SilentTemplateDeleteProps> = ({ selection, onExit }) => {
  const { audienceId } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const runner = useSmsConnectorFlowRunner('templateDelete', {
    audienceId,
    lang: currentLanguage,
  });
  return <SilentTemplateDeleteBody selection={selection} onExit={onExit} runner={runner} />;
};

const EmailSilentTemplateDelete: FC<SilentTemplateDeleteProps> = ({ selection, onExit }) => {
  const { audienceId } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const runner = useEmailConnectorFlowRunner('templateDelete', {
    audienceId,
    lang: currentLanguage,
  });
  return <SilentTemplateDeleteBody selection={selection} onExit={onExit} runner={runner} />;
};

/**
 * After the user confirms delete, run Delete Template with autofill/skip so no dropdown is shown.
 */
export const SilentTemplateDelete: FC<SilentTemplateDeleteProps> = ({ selection, onExit }) => {
  switch (selection.channel) {
    case 'whatsapp':
      return <WhatsappSilentTemplateDelete selection={selection} onExit={onExit} />;
    case 'sms':
      return <SmsSilentTemplateDeleteInner selection={selection} onExit={onExit} />;
    case 'email':
      return <EmailSilentTemplateDelete selection={selection} onExit={onExit} />;
  }
};
